import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { wabaApi, WABA_BASE } from "../../lib/wabaApi";
import { DialogPanel } from "../shared/DialogPanel";
import { XIcon } from "../shared/Icons";
import { backdropClose } from "../shared/backdrop";
import { CallSettingsPanel } from "./CallSettingsPanel";

/**
 * Softphone de teste da Calling API (provisório até o CRM implementar).
 *
 * Consome o motor EXATAMENTE como o CRM consumirá: REST instance-auth
 * (x-instance-id + x-api-key) + WebSocket /calls/ws. O browser é o peer WebRTC
 * — o áudio flui direto entre esta aba e a borda da Meta (o motor só sinaliza).
 * Gravação: feita pela Meta (`record` no connect/accept); o motor baixa pro R2.
 */

interface Props { connectionId: string; instanceId: string; onClose: () => void }

interface CallRow {
  id: string; call_id: string; direction: string; from_number: string | null;
  to_number: string | null; contact_name: string | null; status: string;
  duration_seconds: number | null; has_recording: boolean; voicemail_url: string | null; created_at: string;
}

type Phase = "idle" | "dialing" | "incoming" | "in-call";

const STATUS_PT: Record<string, string> = {
  ringing: "Tocando", accepted: "Em chamada", rejected: "Rejeitada",
  completed: "Concluída", missed: "Perdida", failed: "Falhou",
};

function fmtTime(iso: string) {
  const utc = iso.endsWith("Z") || iso.includes("+") ? iso : iso + "Z";
  return new Date(utc).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "America/Sao_Paulo" });
}

/** Espera o ICE gathering completar — a Meta não faz trickle ICE, o SDP vai inteiro. */
function waitIce(pc: RTCPeerConnection): Promise<void> {
  if (pc.iceGatheringState === "complete") return Promise.resolve();
  return new Promise(res => {
    const done = () => { if (pc.iceGatheringState === "complete") { pc.removeEventListener("icegatheringstatechange", done); res(); } };
    pc.addEventListener("icegatheringstatechange", done);
    setTimeout(res, 3000); // fallback: manda o que juntou
  });
}

export function CallConsole({ connectionId, instanceId, onClose }: Props) {
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [wsOk, setWsOk] = useState(false);
  const [phase, setPhase] = useState<Phase>("idle");
  const [statusLine, setStatusLine] = useState("");
  const [error, setError] = useState("");
  const [phone, setPhone] = useState("");
  const [permBody, setPermBody] = useState("");
  const [permState, setPermState] = useState("");
  const [callingEnabled, setCallingEnabled] = useState<boolean | null>(null);
  const [record, setRecord] = useState(true);
  const [playingUrl, setPlayingUrl] = useState<string | null>(null);

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const callIdRef = useRef<string | null>(null);
  const micRef = useRef<MediaStream | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const incomingRef = useRef<{ callId: string; sdp: string; from: string } | null>(null);

  // instance-auth: o mesmo que o CRM usará. A api-key vem do endpoint de painel.
  const hdrs = (): Record<string, string> => ({
    "Content-Type": "application/json", "x-instance-id": instanceId, "x-api-key": apiKey ?? "",
  });
  async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
    const res = await fetch(`${WABA_BASE}${path}`, { ...init, headers: hdrs() });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      const d = (body as any)?.detail;
      throw new Error(typeof d === "string" ? d : d?.error ?? res.statusText);
    }
    return body as T;
  }

  useEffect(() => {
    wabaApi.get<{ api_key: string }>(`/connections/${connectionId}/api-key`)
      .then(d => setApiKey(d.api_key))
      .catch(() => setError("Não foi possível obter a API key da conexão"));
  }, [connectionId]);

  // WebSocket de eventos (toca chamada entrante, entrega SDP answer, statuses)
  useEffect(() => {
    if (!apiKey) return;
    const url = `${WABA_BASE.replace("https", "wss")}/calls/ws?instance_id=${instanceId}&api_key=${encodeURIComponent(apiKey)}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;
    ws.onopen = () => setWsOk(true);
    ws.onclose = () => setWsOk(false);
    ws.onmessage = ev => {
      try { handleEvent(JSON.parse(ev.data)); } catch { /* ignora frame inválido */ }
    };
    return () => ws.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey]);

  // settings: estado atual do calling no número
  useEffect(() => {
    if (!apiKey) return;
    api<any>(`/calls/settings`).then(s => {
      setCallingEnabled((s?.calling?.status ?? "").toUpperCase() === "ENABLED");
    }).catch(() => setCallingEnabled(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey]);

  const { data: logData, refetch: refetchLog } = useQuery({
    queryKey: ["waba-calls", connectionId, apiKey],
    queryFn: () => api<{ total: number; calls: CallRow[] }>(`/calls?limit=20`),
    enabled: !!apiKey,
    refetchInterval: 5000,
  });

  function handleEvent(value: any) {
    for (const c of value?.calls ?? []) {
      if (c.event === "connect" && c.direction !== "BUSINESS_INITIATED" && c.session?.sdp) {
        // chamada ENTRANDO (user-initiated): guarda o offer e toca
        incomingRef.current = { callId: c.id, sdp: c.session.sdp, from: c.from };
        setPhase("incoming");
        setStatusLine(`Chamada de ${c.from}`);
      } else if (c.event === "connect" && c.session?.sdp && pcRef.current) {
        // outbound: answer da Meta (cliente atendeu)
        pcRef.current.setRemoteDescription({ type: "answer", sdp: c.session.sdp })
          .then(() => { setPhase("in-call"); setStatusLine("Em chamada"); })
          .catch(e => setError(`setRemoteDescription: ${e}`));
      } else if (c.event === "terminate") {
        if (c.id === callIdRef.current || phase !== "idle") finishCall(false);
        setStatusLine(`Encerrada (${STATUS_PT[(c.status ?? "").toLowerCase()] ?? c.status ?? ""} · ${c.duration ?? 0}s)`);
        refetchLog();
      }
    }
    for (const s of value?.statuses ?? []) {
      if (s.type !== "call") continue;
      const st = (s.status ?? "").toUpperCase();
      if (st === "RINGING") setStatusLine("Tocando no WhatsApp do cliente...");
      if (st === "ACCEPTED") setStatusLine("Cliente atendeu, conectando áudio...");
      if (st === "REJECTED") { setStatusLine("Cliente rejeitou a chamada"); finishCall(false); }
      refetchLog();
    }
  }

  async function setupPeer(): Promise<RTCPeerConnection> {
    const mic = await navigator.mediaDevices.getUserMedia({ audio: true });
    micRef.current = mic;
    const pc = new RTCPeerConnection({ iceServers: [{ urls: "stun:stun.l.google.com:19302" }] });
    pcRef.current = pc;
    mic.getTracks().forEach(t => pc.addTrack(t, mic));
    pc.ontrack = ev => {
      const remote = ev.streams[0] ?? new MediaStream([ev.track]);
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = remote;
        remoteAudioRef.current.play().catch(() => {});
      }
    };
    return pc;
  }

  /** Encerra local: fecha peer/mic. terminate=true → avisa a Meta. */
  async function finishCall(terminate: boolean) {
    const callId = callIdRef.current;
    if (terminate && callId) {
      api(`/calls/${encodeURIComponent(callId)}/terminate`, { method: "POST" }).catch(() => {});
    }
    pcRef.current?.close(); pcRef.current = null;
    micRef.current?.getTracks().forEach(t => t.stop()); micRef.current = null;
    callIdRef.current = null;
    incomingRef.current = null;
    setPhase("idle");
  }

  // cleanup ao fechar o modal no meio de uma chamada
  useEffect(() => () => { finishCall(true); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function dial() {
    setError("");
    try {
      setPhase("dialing");
      setStatusLine("Preparando áudio...");
      const pc = await setupPeer();
      const offer = await pc.createOffer({ offerToReceiveAudio: true });
      await pc.setLocalDescription(offer);
      await waitIce(pc);
      setStatusLine("Discando...");
      const r = await api<{ call_id: string }>(`/calls/connect`, {
        method: "POST", body: JSON.stringify({ to: phone, sdp: pc.localDescription!.sdp, record }),
      });
      callIdRef.current = r.call_id;
      refetchLog();
    } catch (e: any) { setError(e.message); finishCall(false); }
  }

  async function answer() {
    const inc = incomingRef.current;
    if (!inc) return;
    setError("");
    try {
      callIdRef.current = inc.callId;
      const pc = await setupPeer();
      await pc.setRemoteDescription({ type: "offer", sdp: inc.sdp });
      const ans = await pc.createAnswer();
      await pc.setLocalDescription(ans);
      await waitIce(pc);
      const sdp = pc.localDescription!.sdp;
      const cid = encodeURIComponent(inc.callId);
      // pre_accept estabelece o WebRTC antes do accept (evita clipping)
      await api(`/calls/${cid}/pre-accept`, { method: "POST", body: JSON.stringify({ sdp }) });
      await api(`/calls/${cid}/accept`, { method: "POST", body: JSON.stringify({ sdp, record }) });
      setPhase("in-call");
      setStatusLine("Em chamada");
      refetchLog();
    } catch (e: any) { setError(e.message); finishCall(false); }
  }

  async function reject() {
    const inc = incomingRef.current;
    if (!inc) return;
    try { await api(`/calls/${encodeURIComponent(inc.callId)}/reject`, { method: "POST" }); } catch { /* já caiu */ }
    incomingRef.current = null;
    setPhase("idle");
    setStatusLine("");
    refetchLog();
  }

  async function toggleCalling() {
    setError("");
    try {
      await api(`/calls/settings`, {
        method: "POST",
        body: JSON.stringify({ calling: { status: callingEnabled ? "DISABLED" : "ENABLED" } }),
      });
      setCallingEnabled(v => !v);
    } catch (e: any) { setError(e.message); }
  }

  async function requestPermission() {
    setError("");
    try {
      await api(`/calls/permissions/request`, {
        method: "POST", body: JSON.stringify({ to: phone, body: permBody || undefined }),
      });
      setPermState("Pedido enviado — aguardando resposta do cliente");
    } catch (e: any) { setError(e.message); }
  }

  async function checkPermission() {
    setError("");
    try {
      const p = await api<any>(`/calls/permissions/${encodeURIComponent(phone)}`);
      if (!p.call_permission) setPermState("Nunca respondeu a um pedido");
      else if (p.call_permission === "accept")
        setPermState(`Permitido${p.call_permission_permanent ? " (permanente)" : p.call_permission_expires_at ? ` até ${fmtTime(p.call_permission_expires_at)}` : ""}`);
      else setPermState("Recusou o último pedido");
    } catch (e: any) { setError(e.message); }
  }

  async function playRecording(callId: string) {
    setError("");
    try {
      const r = await api<{ url: string }>(`/calls/${encodeURIComponent(callId)}/recording`);
      setPlayingUrl(r.url);
    } catch (e: any) { setError(e.message); }
  }

  const inputStyle: React.CSSProperties = { padding: "6px 10px", background: "var(--surface-input)", border: "1px solid var(--line)", borderRadius: 6, color: "var(--ink)", fontSize: 13, outline: "none" };
  const btn = (color: string, bg = "transparent", border = "var(--line-bold)"): React.CSSProperties =>
    ({ background: bg, border: `1px solid ${border}`, borderRadius: 8, color, fontSize: 12, padding: "7px 14px", cursor: "pointer", fontWeight: 600 });

  const calls = logData?.calls ?? [];
  const busy = phase !== "idle";

  return (
    <div style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any }}
      {...backdropClose(() => { onClose(); })}>
      <DialogPanel onClose={onClose} label="Chamadas (Calling API)" style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 12, padding: 24, width: 640, maxHeight: "88vh", display: "flex", flexDirection: "column", gap: 14, overflow: "auto" }}>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>
            Chamadas <span style={{ fontSize: 11, fontWeight: 400, color: "var(--ink-muted)" }}>(dash de teste do motor)</span>
          </span>
          <button aria-label="Fechar" onClick={onClose} style={{ background: "transparent", border: "none", color: "var(--ink-muted)", cursor: "pointer" }}><XIcon size={16} /></button>
        </div>

        {/* Status do serviço */}
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", fontSize: 12, color: "var(--ink-secondary)" }}>
          <span>WS: <b style={{ color: wsOk ? "var(--ok)" : "var(--danger)" }}>{wsOk ? "conectado" : "desconectado"}</b></span>
          <span>Calling no número: <b style={{ color: callingEnabled ? "var(--ok)" : "var(--ink-muted)" }}>{callingEnabled == null ? "?" : callingEnabled ? "habilitado" : "desabilitado"}</b></span>
          <button style={btn("var(--ink-secondary)")} onClick={toggleCalling}>
            {callingEnabled ? "Desabilitar" : "Habilitar"} calling
          </button>
          <label style={{ display: "flex", gap: 5, alignItems: "center", cursor: "pointer", marginLeft: "auto" }}>
            <input type="checkbox" checked={record} onChange={e => setRecord(e.target.checked)} /> Gravar chamadas
          </label>
        </div>

        {error && <div style={{ fontSize: 12, color: "var(--danger)", borderRadius: 6, padding: "6px 10px", border: "1px solid var(--line)" }}>{error}</div>}

        {/* Chamada entrante */}
        {phase === "incoming" && (
          <div style={{ display: "flex", gap: 10, alignItems: "center", padding: "10px 14px", border: "1px solid var(--ok-line)", background: "var(--ok-soft)", borderRadius: 8 }}>
            <span style={{ fontSize: 13, color: "var(--ink)", fontWeight: 600 }}>📞 {statusLine}</span>
            <button style={{ ...btn("var(--ok)", "transparent", "var(--ok-line)"), marginLeft: "auto" }} onClick={answer}>Atender</button>
            <button style={btn("var(--danger)", "transparent", "var(--line-strong)")} onClick={reject}>Rejeitar</button>
          </div>
        )}

        {/* Discador */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8, borderTop: "1px solid var(--line)", paddingTop: 12 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-secondary)" }}>Contato (telefone, bsuid ou username)</span>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input style={{ ...inputStyle, width: 190 }} placeholder="5542999998888" value={phone} onChange={e => setPhone(e.target.value)} disabled={busy} />
            {phase === "idle" && <button style={btn("var(--ok)", "transparent", "var(--ok-line)")} disabled={!phone || !apiKey} onClick={dial}>Ligar</button>}
            {(phase === "dialing" || phase === "in-call") && (
              <button style={btn("var(--danger)", "transparent", "var(--line-strong)")} onClick={() => finishCall(true)}>Desligar</button>
            )}
            <button style={btn("var(--ink-secondary)")} disabled={!phone} onClick={checkPermission}>Ver permissão</button>
          </div>
          {busy && phase !== "incoming" && <span style={{ fontSize: 12, color: "var(--info)" }}>{statusLine}</span>}
          {!busy && statusLine && <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>{statusLine}</span>}

          {/* Permissão */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <input style={{ ...inputStyle, flex: 1, minWidth: 220 }} placeholder="Texto do pedido (opcional)" value={permBody} onChange={e => setPermBody(e.target.value)} />
            <button style={btn("var(--ink-secondary)")} disabled={!phone} onClick={requestPermission}>Pedir permissão</button>
          </div>
          {permState && <span style={{ fontSize: 12, color: "var(--ink-secondary)" }}>{permState}</span>}
        </div>

        {apiKey && <CallSettingsPanel api={api} />}

        {/* Player da gravação */}
        {playingUrl && (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <audio controls autoPlay src={playingUrl} style={{ flex: 1, height: 34 }} />
            <button style={btn("var(--ink-muted)")} onClick={() => setPlayingUrl(null)}>Fechar</button>
          </div>
        )}

        {/* Log de chamadas */}
        <div style={{ borderTop: "1px solid var(--line)", paddingTop: 12, display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-secondary)", marginBottom: 4 }}>Últimas chamadas</span>
          {calls.length === 0 && <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>Nenhuma chamada ainda.</span>}
          {calls.map(c => (
            <div key={c.id} style={{ display: "flex", gap: 10, alignItems: "center", background: "var(--surface-input)", border: "1px solid var(--line)", borderRadius: 6, padding: "7px 10px", fontSize: 12 }}>
              <span style={{ color: c.direction === "inbound" ? "var(--info)" : "var(--violet)", fontWeight: 600 }}>
                {c.direction === "inbound" ? "↓ Recebida" : "↑ Feita"}
              </span>
              <span style={{ color: "var(--ink)" }}>{c.direction === "inbound" ? c.from_number : c.to_number}{c.contact_name ? ` · ${c.contact_name}` : ""}</span>
              <span style={{ color: ["failed", "rejected", "missed"].includes(c.status) ? "var(--danger)" : "var(--ink-secondary)" }}>
                {STATUS_PT[c.status] ?? c.status}{c.duration_seconds != null ? ` · ${c.duration_seconds}s` : ""}
              </span>
              <span style={{ marginLeft: "auto", color: "var(--ink-muted)" }}>{fmtTime(c.created_at)}</span>
              {c.has_recording && (
                <button style={{ background: "transparent", border: "none", color: "var(--info)", cursor: "pointer", fontSize: 12 }} onClick={() => playRecording(c.call_id)}>
                  ▶ gravação
                </button>
              )}
              {c.voicemail_url && (
                <button style={{ background: "transparent", border: "none", color: "var(--info)", cursor: "pointer", fontSize: 12 }} onClick={() => setPlayingUrl(c.voicemail_url)}>
                  ▶ recado
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Áudio remoto da chamada */}
        <audio ref={remoteAudioRef} autoPlay style={{ display: "none" }} />
      </DialogPanel>
    </div>
  );
}
